import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import BreadcrumbsBar from "../components/BreadcrumbsBar";
import { LineChart } from "@mui/x-charts/LineChart";
import axios from "axios";

const metricCards = [
  {
    key: "uptime",
    label: "System Uptime",
    color: "#22c55e",
    icon: <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 38 }} />,
    subtitle: "Last 24h",
  },
  {
    key: "errors",
    label: "Recent Error Rate",
    color: "#eab308",
    icon: <ErrorIcon sx={{ color: "#eab308", fontSize: 38 }} />,
    subtitle: "Past Hour",
  },
  {
    key: "rpm",
    label: "API Requests / Min",
    color: "#3b82f6",
    icon: <QueryStatsIcon sx={{ color: "#3b82f6", fontSize: 38 }} />,
    subtitle: "Current",
  },
];

const SystemHealthPage = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({
    uptime: null,
    errors: null,
    rpm: null,
  });
  const [rpmTrend, setRpmTrend] = useState([]); // For chart
  const [errorTrend, setErrorTrend] = useState([]); // For chart
  const [recentLogs, setRecentLogs] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch status and trend data
  const fetchStatus = async () => {
    setLoading(true);
    try {
      const [uptimeRes, errorRes, rpmRes] = await Promise.all([
        axios
          .get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/system/uptime`)
          .then((res) => res.data),
        axios
          .get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/system/errors`)
          .then((res) => res.data),
        axios
          .get(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/system/requests-per-minute`
          )
          .then((res) => res.data),
      ]);
      setStatus({
        uptime: uptimeRes.uptimePercent ?? "N/A",
        errors: errorRes.count ?? "N/A",
        rpm: rpmRes.rpm ?? "N/A",
      });
      setRecentLogs(errorRes.errors ? errorRes.errors.slice(0, 8) : []);
      setRpmTrend(rpmRes.trend || []);
      setErrorTrend(errorRes.trend || []);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (e) {
      setStatus({ uptime: "N/A", errors: "N/A", rpm: "N/A" });
      setRecentLogs([]);
      setRpmTrend([]);
      setErrorTrend([]);
    }
    setLoading(false);
  };

  // Initial and auto refresh
  useEffect(() => {
    fetchStatus();

    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);
  const rpmChartDataset = rpmTrend.map((item) => ({
    time: item.minute.slice(-5),
    value: item.count,
  }));
  useEffect(() => {
    console.log("rpmTrend updated", rpmTrend);
    console.log("rpmTrend updated", rpmChartDataset);
  }, [rpmTrend, rpmChartDataset]);

  // Format data for x-charts
  const errorChartDataset = errorTrend.map((item) => ({
    time: item.minute.slice(-5),
    value: item.count,
  }));

  const formatValue = (key, value) => {
    if (value === null || value === undefined || value === "N/A") return "N/A";
    if (key === "uptime") return `${Math.round(value * 100) / 100}%`;
    if (key === "errors")
      return value === 0
        ? "No Errors"
        : `${value} Error${value > 1 ? "s" : ""}`;
    return value;
  };

  // Table columns for logs
  const renderLogs = () => (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 3,
        boxShadow: 0,
        minWidth: 400,
        maxHeight: 280,
      }}
    >
      <Table size="small" stickyHeader>
        <TableBody>
          {recentLogs.map((log, idx) => (
            <TableRow
              key={idx}
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
                background: idx % 2 === 0 ? "#1C2235" : "#181D2F",
              }}
            >
              <TableCell sx={{ color: "#8F93A2", border: 0, fontSize: 13 }}>
                {log.timestamp
                  ? new Date(log.timestamp).toLocaleTimeString()
                  : "--:--"}
              </TableCell>
              <TableCell
                sx={{
                  color: log.status >= 500 ? "#F87171" : "#FFAB00",
                  border: 0,
                  fontSize: 14,
                  fontWeight: 600,
                  maxWidth: 360,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={
                  log.message || `${log.method} ${log.path} (${log.status})`
                }
              >
                {log.message || `${log.method} ${log.path} (${log.status})`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box className="main-container mt-4" sx={{ width: "100%", pb: 6 }}>
      <BreadcrumbsBar />
      <Box display="flex" justifySelf="right" mb={2}>
        <Box>
          <Typography variant="caption" sx={{ mr: 2, fontWeight: 500 }}>
            {lastUpdated && `Last updated: ${lastUpdated}`}
          </Typography>
          <Button
            onClick={fetchStatus}
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={loading}
            sx={{
              borderColor: "#6366f1",
              color: "#fff",
              "&:hover": {
                borderColor: "#3b82f6",
                background: "#3b82f620",
              },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      <Divider sx={{ borderColor: "#222", mb: 3 }} />

      <Grid container spacing={3} columns={{ xs: 12, sm: 12, md: 12 }}>
        <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
          <Grid container spacing={5} columns={{ xs: 12, sm: 12, md: 12 }}>
            <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
              <Grid container spacing={3} columns={{ xs: 12, sm: 12, md: 12 }}>
                {metricCards.map((metric) => (
                  <Grid item size={{ xs: 12, sm: 12, md: 4 }} key={metric.key}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        boxShadow: 8,
                        borderLeft: `8px solid ${metric.color}`,
                        minHeight: 140,
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        px: 3,
                        py: 2,
                        "&:hover": { boxShadow: 16 },
                      }}
                    >
                      <CardContent sx={{ flex: 1 }}>
                        <Box display={"flex"} alignItems={"center"} mb={2}>
                          <Box mr={1}>{metric.icon}</Box>
                          <Typography
                            variant="h4"
                            sx={{
                              color: metric.color,
                              fontWeight: 900,
                              letterSpacing: "-1.5px",
                              mb: 0.5,
                            }}
                          >
                            {formatValue(metric.key, status[metric.key])}
                          </Typography>
                        </Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 0.5,
                            textTransform: "uppercase",
                          }}
                        >
                          {metric.label}
                        </Typography>

                        <Typography sx={{ color: "#6C7383", fontSize: 16 }}>
                          {metric.subtitle}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
              <Box
                sx={{
                  borderRadius: 3,
                  boxShadow: 2,
                  p: 2,
                  height: "40vh",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography fontWeight={700} mb={1}>
                  API Requests / Min (10min)
                </Typography>
                <Box flex={1} display="flex" alignItems="center">
                  {rpmChartDataset.length === 0 ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="100%"
                      height="100%"
                    >
                      <CircularProgress color="info" />
                    </Box>
                  ) : (
                    <LineChart
                      dataset={rpmChartDataset}
                      // series={[{ data: uData, label: 'uv', area: true, showMark: false }]}
                      // xAxis={[{scaleType:"point", data:xLabels}]}
                      series={[
                        {
                          // dataKey: "value",
                          // color: "#3b82f6",
                          // label: "Requests",
                          dataKey: "value",
                          color: "#60a5fa", // yellow color like screenshot
                          label: "Requests",
                          area: true, // fill under the line
                          curve: "catmullRom",
                          showMark: false
                        },
                      ]}
                      height={280}
                      // sx={{ ".MuiLineElement-root": { strokeWidth: 3 } }}
                      sx={{
                        ".MuiLineElement-root": { strokeWidth: 1 },
                        ".MuiAreaElement-root": {
                          fill: "url(#rpm-gradient)",
                          opacity: 0.75,
                        },
                      }}
                      grid={{ vertical: true, horizontal: true }}
                      slotProps={{
                        legend: { hidden: true },
                      }}
                    >
                      <defs>
                        <linearGradient
                          id="rpm-gradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#60a5fa"
                            stopOpacity="0.9"
                          />
                          <stop
                            offset="100%"
                            stopColor="#9eb2f8ff"
                            stopOpacity="0.1"
                          />
                        </linearGradient>
                      </defs>
                    </LineChart>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
          <Grid container spacing={1} columns={{ xs: 12, sm: 12, md: 12 }}>
            <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
              <Box
                sx={{
                  borderRadius: 3,
                  boxShadow: 2,
                  p: 2,
                  height: "30vh",
                  display: "flex",
                  flexDirection: "column",
                }}
                elevation={2}
              >
                <Typography fontWeight={700} mb={1}>
                  Error Events (10min)
                </Typography>
                <Box flex={1} display="flex" alignItems="center">
                  {errorChartDataset.length === 0 ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="100%"
                      height="100%"
                    >
                      <Typography> No Error</Typography>
                    </Box>
                  ) : (
                    <LineChart
                      dataset={errorChartDataset}
                      xAxis={[
                        { dataKey: "time", label: "Time", stroke: "#aaa" },
                      ]}
                      series={[
                        {
                          dataKey: "value",
                          color: "#eab308",
                          label: "Errors",
                        },
                      ]}
                      height={180}
                      sx={{ ".MuiLineElement-root": { strokeWidth: 3 } }}
                      grid={{ vertical: true, horizontal: true }}
                      slotProps={{
                        legend: { hidden: true },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
              <Box
                sx={{
                  borderRadius: 3,
                  boxShadow: 2,
                  p: 3,
                  mt: 4,
                  minHeight: 210,
                  height: "30vh",
                }}
              >
                <Typography fontWeight={700} mb={2}>
                  Recent Events
                </Typography>
                {loading ? (
                  <Box display="flex" alignItems="center" height={80}>
                    <CircularProgress color="inherit" />
                  </Box>
                ) : recentLogs.length === 0 ? (
                  <Typography color="#6C7383">No recent errors.</Typography>
                ) : (
                  renderLogs()
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemHealthPage;
